'use client';

import { cn } from '../../lib/utils';
import { ImageLightbox } from './image-lightbox';
import {
  DEFAULT_IMAGE_VIEWER_TRANSLATIONS,
  type ImageAspectRatio,
  type ImageViewerTranslations,
  type ViewerImage,
} from './types';
import { useImageViewer } from './use-image-viewer';

interface ImageGalleryProps {
  images: ViewerImage[];
  /** Aspect ratio of the inline hero. Defaults to a square. */
  aspectRatio?: ImageAspectRatio;
  /** Number of thumbnail columns in the strip. Defaults to 6. */
  thumbnailColumns?: number;
  /** How the inline hero fills its box. `cover` crops; `contain` shows all. Defaults to `cover`. */
  heroFit?: 'cover' | 'contain';
  translations?: ImageViewerTranslations;
  className?: string;
}

const ASPECT_CLASS: Record<ImageAspectRatio, string> = {
  auto: '',
  '1/1': 'aspect-square',
  '4/3': 'aspect-[4/3]',
  '3/4': 'aspect-[3/4]',
  '16/9': 'aspect-video',
};

/**
 * Inline image gallery: a large hero with a thumbnail filmstrip beneath.
 * Clicking the hero (or any thumbnail twice) opens the immersive
 * {@link ImageLightbox}. Storage-decoupled — pass display-ready `src` URLs.
 */
export function ImageGallery({
  images,
  aspectRatio = '1/1',
  thumbnailColumns = 6,
  heroFit = 'cover',
  translations,
  className,
}: ImageGalleryProps) {
  const t = { ...DEFAULT_IMAGE_VIEWER_TRANSLATIONS, ...translations };
  const viewer = useImageViewer(images);

  if (images.length === 0) {
    return (
      <div
        className={cn(
          'bg-muted text-muted-foreground flex items-center justify-center rounded-lg text-sm',
          ASPECT_CLASS[aspectRatio] || 'aspect-square',
          className,
        )}
      >
        {t.noImage}
      </div>
    );
  }

  const hero = images[viewer.index]!;

  return (
    <div className={cn('space-y-3', className)}>
      <button
        type="button"
        onClick={() => viewer.openAt(viewer.index)}
        aria-label={hero.alt ?? hero.fileName ?? t.fullscreen}
        className={cn(
          'bg-muted focus-visible:ring-ring relative block w-full overflow-hidden rounded-lg focus:outline-none focus-visible:ring-2',
          ASPECT_CLASS[aspectRatio],
        )}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={hero.src}
          alt={hero.alt ?? hero.fileName ?? ''}
          className={cn(
            'absolute inset-0 h-full w-full',
            heroFit === 'contain' ? 'object-contain' : 'object-cover',
          )}
        />
      </button>

      {images.length > 1 && (
        <div
          className="grid gap-2"
          style={{
            gridTemplateColumns: `repeat(${thumbnailColumns}, minmax(0, 1fr))`,
          }}
        >
          {images.map((image, i) => (
            <button
              key={image.id ?? image.src}
              type="button"
              onClick={() => viewer.goTo(i)}
              aria-pressed={viewer.index === i}
              className={cn(
                'bg-muted relative aspect-square overflow-hidden rounded border-2',
                viewer.index === i
                  ? 'border-primary'
                  : 'border-transparent opacity-70 hover:opacity-100',
              )}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={image.thumbnailSrc ?? image.src}
                alt={image.alt ?? image.fileName ?? ''}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      <ImageLightbox
        images={images}
        index={viewer.index}
        onIndexChange={viewer.goTo}
        open={viewer.isOpen}
        onOpenChange={viewer.setIsOpen}
        translations={translations}
      />
    </div>
  );
}
